import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import { Add, Remove, Check, Close } from '@mui/icons-material';
//import SettingsIcon from '@mui/icons-material/Settings';
import SettingsIcon from '@mui/icons-material/Compress';
import { blur } from '@techstark/opencv-js';

type DualSpinnerProps = {
  value1: number;
  value2: number;

  onChange1: (v: number) => void;
  onChange2: (v: number) => void;

  onOpen?: () => void;
  onClose?: () => void;

  min1?: number;
  max1?: number;
  step1?: number;

  min2?: number;
  max2?: number;
  step2?: number;

  label1?: string;
  label2?: string;
};

const DimensionSpinner: React.FC<DualSpinnerProps> = ({
  value1,
  value2,
  onChange1,
  onChange2,

  onOpen,
  onClose,

  min1 = 0,
  max1 = 100,
  step1 = 1,

  min2 = 0,
  max2 = 100,
  step2 = 1,

  label1 = 'Param1',
  label2 = 'Param2',
}) => {

  const [isEditing, setIsEditing] = useState(false);

  const [temp1, setTemp1] = useState<number>(value1);
  const [temp2, setTemp2] = useState<number>(value2);
  const holdTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdInterval = useRef<ReturnType<typeof setInterval> | null>(null);


  const containerRef = useRef<HTMLDivElement>(null);

  const clampFloor = (v: number, min: number, max: number) => {
    const floored = step1 >= 1 ? Math.round(v) : Math.round(v * 100) / 100;
    return Math.min(max, Math.max(min, floored));
  };

  const handleApply = () => {
    if (temp1 !== value1) {
      onChange1(temp1);
    }
    if (temp2 !== value2) {
      onChange2(temp2);
    }
    setIsEditing(false);
    onClose?.();
  };


  const handleCancel = () => {
    setTemp1(value1);
    setTemp2(value2);
    setIsEditing(false);
    onClose?.();
  };

  const stopHold = () => {
    if (holdTimeout.current) {
      clearTimeout(holdTimeout.current);
      holdTimeout.current = null;
    }

    if (holdInterval.current) {
      clearInterval(holdInterval.current);
      holdInterval.current = null;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isEditing &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        stopHold();
        handleCancel();
      }
    };

    document.addEventListener('pointerdown', handleClickOutside);
    return () => {
      document.removeEventListener('pointerdown', handleClickOutside);
      stopHold();
    };
  }, [isEditing, value1, value2]);

  useEffect(() => {
    setTemp1(value1);
  }, [value1]);

  useEffect(() => {
    setTemp2(value2);
  }, [value2]);

  const renderSpinner = (
    value: number,
    setValue: React.Dispatch<React.SetStateAction<number>>,
    min: number,
    max: number,
    step: number
  ) => {
    const startHold = (direction: 'inc' | 'dec') => {
      stopHold();

      const action = () => {
        setValue((prev) =>
          clampFloor(prev + (direction === 'inc' ? step : -step), min, max)
        );
      };

      action();

      holdTimeout.current = setTimeout(() => {
        holdInterval.current = setInterval(action, 60);
      }, 300);
    };

    const clampWithStep = (v: number): number => {
      if (step <= 0) {
        return Math.min(max, Math.max(min, v));
      }

      const decimals = (step.toString().split('.')[1] || '').length;
      const factor = Math.pow(10, decimals);

      const vInt = Math.round(v * factor);
      const stepInt = Math.round(step * factor);

      const nearestMultiple = Math.round(vInt / stepInt) * stepInt;
      let result = nearestMultiple / factor;

      result = parseFloat(result.toFixed(decimals));

      return Math.min(max, Math.max(min, result));
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      let parsedDecValue = parseDecimal(event.target.value);
      if (parsedDecValue !== null) {
        setValue(clampWithStep(parsedDecValue));
      }
    };

    const parseDecimal = (str: string): number | null => {
      const cleaned = str.trim().replace(/,/g, '.');
      if (cleaned === '' || cleaned === '-' || cleaned === '.') {
        return null;
      }
      const num = parseFloat(cleaned);
      return isNaN(num) ? null : num;
    };

    const formatNumber = (num: number, stepVal: number): string => {
      if (stepVal % 1 === 0) {
        return Math.round(num).toString();
      }
      const decimalPlaces = stepVal.toString().split('.')[1]?.length || 0;
      return num.toFixed(decimalPlaces);
    }

    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.6,
          bgcolor: 'rgba(255, 255, 255, 0.39)',
          borderRadius: '20px',
          padding: '1px',
        }}
      >
        <IconButton
          size="small"
          onPointerDown={(e) => {
            e.preventDefault();
            startHold('dec');
          }}
          onPointerUp={stopHold}
          onPointerLeave={stopHold}
          onPointerCancel={stopHold}
          disabled={value <= min}
          sx={{
            bgcolor: 'transparent',
            color: 'var(--toolbar-button-border)',
            width: 24,
            height: 24,
            borderRadius: '12px',
            touchAction: 'none',
          }}
        >
          <Remove sx={{ fontSize: 14 }} />
        </IconButton>

        <TextField
          type="text"
          value={formatNumber(value, step)}
          onChange={handleInputChange}
          variant="standard"
          size="small"
          InputProps={{
            disableUnderline: true,
            sx: {
              width: '44px',
              '& input': {
                textAlign: 'center',
                padding: '2px 0',
                fontSize: '11px',
                color: 'var(--toolbar-button-border)',
                bgcolor: 'transparent',
                fontFamily: 'monospace',
                fontWeight: 500,
              },
            },
          }}
        />

        <IconButton
          size="small"
          onPointerDown={(e) => {
            e.preventDefault();
            startHold('inc');
          }}
          onPointerUp={stopHold}
          onPointerLeave={stopHold}
          onPointerCancel={stopHold}
          disabled={value >= max}
          sx={{
            bgcolor: 'transparent',
            color: 'var(--toolbar-button-border)',
            width: 24,
            height: 24,
            borderRadius: '12px',
            touchAction: 'none',
          }}
        >
          <Add sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    );
  };


  if (!isEditing) {
    return (
      <Box
        data-voice-open="1"
        onClick={() => {
          setTemp1(value1);
          setTemp2(value2);
          setIsEditing(true);
          onOpen?.();
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid var(--toolbar-button-border)',
          borderRadius: '50%',
          bgcolor: 'var(--spinner-bg)',
          backdropFilter: 'blur(8px)',
          width: 26,
          height: 26,
          cursor: 'pointer',
          transition: '0.15s ease',
          '&:hover': { bgcolor: 'var(--spinner-hover-bg)' }
        }}
      >
        <SettingsIcon
          sx={{
            fontSize: 24,
            color: 'var(--toolbar-button-border)',
            transform: 'rotate(90deg)',
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        bgcolor: 'var(--spinner-bg)',
        backdropFilter: 'blur(8px)',
        borderRadius: '12px',
        border: '1px solid var(--toolbar-button-border)',
        padding: '6px 10px',
        minWidth: '200px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        transition: 'all 0.2s ease',
        textAlign: 'center',
        whiteSpace: 'nowrap',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 0.8,
        }}
      >
        <IconButton
          size="medium"
          data-voice-close="1"
          onClick={handleCancel}
          sx={{ color: '#d32f2f', width: 18, height: 18, mb: 0.5 }}
        >
          <Close sx={{ fontSize: 18 }} />
        </IconButton>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
            <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: 'var(--toolbar-button-border)' }}>
              {label1}
            </Typography>
            {renderSpinner(temp1, setTemp1, min1, max1, step1)}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
            <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: 'var(--toolbar-button-border)' }}>
              {label2}
            </Typography>
            {renderSpinner(temp2, setTemp2, min2, max2, step2)}
          </Box>
        </Box>

        <IconButton
          size="small"
          onClick={handleApply}
          sx={{ color: '#2e7d32', width: 18, height: 18, mb: 0.5 }}
        >
          <Check sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default DimensionSpinner;